import { useMemo, useState, useCallback } from 'react'
import type { Notification, NotificationQueryParams } from '@/types'
import {
  useInfiniteNotifications,
  useNotificationCompanyFacets,
} from './useNotifications'
import { useWorkspaceCompanyFilter } from '@/features/companies/components/WorkspaceCompanyFilter'
import { type NotificationCategory } from '../utils/notificationTaxonomy'
import {
  GENERAL_COMPANY_SCOPE,
  type NotificationCompanyTab,
} from '../components/NotificationCompanyTabs'

const PAGE_SIZE = 10

/** Server sentinel for "notifications with no company" — matches the backend. */
const NO_COMPANY_SCOPE = '__none__'

/**
 * A type-filter category maps to one or more backend notification `type`s. The
 * list endpoint accepts a comma-separated `type` list, so every category is
 * filtered and paginated server-side.
 */
export const CATEGORY_TYPES: Record<NotificationCategory, string[]> = {
  shifts: ['SHIFT_ASSIGNED', 'SHIFT_EXTENDED', 'SHIFT_UPDATED', 'SHIFT_REMOVED', 'SHIFT_ALERT', 'SHIFT_ALERT_ESCALATION'],
  attendance: ['SHIFT_CLOCK_IN'],
  leave: ['LEAVE_REQUEST_SUBMITTED', 'LEAVE_APPROVED', 'LEAVE_REJECTED'],
  payroll: [
    'PENALTY_ISSUED',
    'BONUS_ISSUED',
    'SECURITY_DEPOSIT_DEDUCTION',
    'SECURITY_REFUND_PROCESSED',
    'PAYROLL_READY',
    'PAYROLL_DRAFT_CREATED',
    'PAYROLL_CALCULATED',
    'PAYROLL_COMPANY_APPROVED',
    'PAYROLL_BATCH_APPROVED',
    'PAYROLL_BATCH_PAID',
    'PAYROLL_BATCH_REJECTED',
    'PAYROLL_RUN_REJECTED',
    'PAYROLL_CALCULATION_ERRORS',
    'PAYROLL_ADJUSTMENT',
    'SALARY_SLIP_AVAILABLE',
    'EXPENSE_CLAIM_APPROVED',
    'EXPENSE_CLAIM_REJECTED',
    'EXPENSE_CLAIM_REIMBURSED',
  ],
  approvals: ['APPROVAL_PENDING', 'EXPENSE_CLAIM_SUBMITTED'],
  account: [
    'ROLE_CHANGED',
    'ACCOUNT_STATUS_CHANGED',
    'PASSWORD_RESET',
    'USER_INVITED',
    'NEW_DEVICE_SIGN_IN',
    'ACCOUNT_LOCKED',
    'PASSWORD_CHANGED',
  ],
  announcements: ['ANNOUNCEMENT_PUBLISHED'],
  messages: ['CHANNEL_MESSAGE', 'DIRECT_MESSAGE'],
  // `system` is "everything else"; there is no positive type list for it, so it
  // is the one category narrowed on the client over the fetched page.
  system: [],
}

export interface NotificationsView {
  /** All notifications loaded so far (accumulated across scroll pages). */
  items: Notification[]
  isLoading: boolean
  error: Error | null
  companyTabs: NotificationCompanyTab[]
  allTotal: number
  allUnread: number
  selectedCompanyId: string | null
  setSelectedCompanyId: (id: string | null) => void
  /** `''` means "all types". */
  selectedCategory: NotificationCategory | ''
  setSelectedCategory: (category: NotificationCategory | '') => void
  filteredUnreadIds: string[]
  filteredTotal: number
  filteredUnread: number
  markAllFilter: {
    companyId?: string
    types?: string[]
    excludeTypes?: string[]
  }
  /** True while more pages remain to be loaded on scroll. */
  hasMore: boolean
  /** True while the next page is being fetched. */
  isLoadingMore: boolean
  /** Loads the next page; wire to a scroll sentinel. */
  loadMore: () => void
  /** Re-runs the notifications query; wire to an error state's retry. */
  refetch: () => Promise<unknown>
}

export function useNotificationsView(): NotificationsView {
  const { companyNameById } = useWorkspaceCompanyFilter()
  const { data: facets = [] } = useNotificationCompanyFacets()

  const [selectedCompanyId, setSelectedCompanyIdState] = useState<string | null>(
    null
  )
  const [selectedCategory, setSelectedCategoryState] = useState<
    NotificationCategory | ''
  >('')

  // `system` has no positive type list, so it can't be pushed to the server.
  const isClientCategory = selectedCategory === 'system'
  const serverType = useMemo(() => {
    if (selectedCategory === '' || isClientCategory) return undefined
    return CATEGORY_TYPES[selectedCategory].join(',')
  }, [selectedCategory, isClientCategory])

  const companyParam = useMemo(() => {
    if (selectedCompanyId === null) return undefined
    return selectedCompanyId === GENERAL_COMPANY_SCOPE
      ? NO_COMPANY_SCOPE
      : selectedCompanyId
  }, [selectedCompanyId])

  const params = useMemo<Omit<NotificationQueryParams, 'page'>>(
    () => ({
      limit: PAGE_SIZE,
      type: serverType,
      companyId: companyParam,
    }),
    [serverType, companyParam]
  )

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteNotifications(params)

  // Flatten every loaded page into one list; the server total drives paging.
  const loadedData = useMemo(
    () => data?.pages.flatMap((p) => p.data) ?? [],
    [data]
  )
  const serverTotal = data?.pages[0]?.total ?? 0

  // Only `system` needs client narrowing; all other filters are server-applied.
  const items = useMemo(() => {
    if (!isClientCategory) return loadedData
    const known = new Set(Object.values(CATEGORY_TYPES).flat())
    return loadedData.filter((n) => !known.has(n.type))
  }, [loadedData, isClientCategory])

  const filteredUnreadIds = useMemo(
    () => items.filter((n) => !n.isRead).map((n) => n._id),
    [items]
  )

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Counts come from the facet aggregation (exact across all history).
  const { allTotal, allUnread, companyTabs } = useMemo(() => {
    let total = 0
    let unread = 0
    const tabs: NotificationCompanyTab[] = []
    let general: NotificationCompanyTab | null = null
    for (const f of facets) {
      total += f.total
      unread += f.unread
      if (f.companyId === null) {
        general = {
          id: GENERAL_COMPANY_SCOPE,
          name: 'General',
          total: f.total,
          unread: f.unread,
        }
      } else {
        tabs.push({
          id: f.companyId,
          name: companyNameById.get(f.companyId) ?? 'Unknown company',
          total: f.total,
          unread: f.unread,
        })
      }
    }
    tabs.sort((a, b) => b.unread - a.unread || a.name.localeCompare(b.name))
    if (general) tabs.push(general)
    return { allTotal: total, allUnread: unread, companyTabs: tabs }
  }, [facets, companyNameById])

  // Server total is exact except for the client-narrowed `system` category,
  // where we can only count what's been loaded so far.
  const filteredTotal = isClientCategory ? items.length : serverTotal
  const filteredUnread = filteredUnreadIds.length
  const markAllFilter = useMemo(() => ({
    ...(companyParam ? { companyId: companyParam } : {}),
    ...(selectedCategory && !isClientCategory
      ? { types: CATEGORY_TYPES[selectedCategory] }
      : {}),
    ...(isClientCategory
      ? { excludeTypes: [...new Set(Object.values(CATEGORY_TYPES).flat())] }
      : {}),
  }), [companyParam, selectedCategory, isClientCategory])

  // Changing a filter changes the query key, so react-query resets paging to
  // page 1 automatically — no manual page reset needed.
  const setSelectedCompanyId = useCallback((id: string | null) => {
    setSelectedCompanyIdState(id)
  }, [])

  const setSelectedCategory = useCallback(
    (category: NotificationCategory | '') => {
      setSelectedCategoryState(category)
    },
    []
  )

  return {
    items,
    isLoading,
    error: (error as Error | null) ?? null,
    companyTabs,
    allTotal,
    allUnread,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedCategory,
    setSelectedCategory,
    filteredUnreadIds,
    filteredTotal,
    filteredUnread,
    markAllFilter,
    // `system` narrows on the client, so "has more" follows the raw server
    // paging; a filtered page may briefly show nothing until more loads.
    hasMore: Boolean(hasNextPage),
    isLoadingMore: isFetchingNextPage,
    loadMore,
    refetch,
  }
}
